import { useEffect, useState } from "react";
import { useFiscalDefaults } from "../../lib/hooks/useFiscalDefaults";
import { ApiClientError } from "../../lib/api/client";
import {
  useRecordPaymentMutation,
  type ExpenseDto,
  type PaymentDto,
} from "../../lib/api/transactions";
import { formatMoney } from "../../lib/format/money";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";
import { buildPaymentLinesFromAmount } from "./transaction-utils";

interface RecordPaymentModalProps {
  open: boolean;
  onClose: () => void;
  expense: ExpenseDto;
  payments: PaymentDto[];
  bankAccounts: { id: string; name: string }[];
  onRecorded: () => void;
}

export function RecordPaymentModal({
  open,
  onClose,
  expense,
  payments,
  bankAccounts,
  onRecorded,
}: RecordPaymentModalProps) {
  const recordPayment = useRecordPaymentMutation();
  const { defaultDate } = useFiscalDefaults();
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [bankAccountId, setBankAccountId] = useState(bankAccounts[0]?.id ?? "");
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && defaultDate) {
      setPaymentDate(defaultDate);
    }
  }, [open, defaultDate]);

  async function confirm() {
    setError(null);
    const paymentAmount = amount || expense.unpaidAmount;
    const lines = buildPaymentLinesFromAmount(expense.lines, payments, paymentAmount);
    try {
      await recordPayment.mutateAsync({
        id: expense.id,
        body: {
          amount: Number.parseFloat(paymentAmount).toFixed(2),
          paymentDate,
          bankAccountId: bankAccountId || bankAccounts[0]?.id,
          reference: reference || undefined,
          lines,
        },
      });
      onRecorded();
      onClose();
      setAmount("");
      setReference("");
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not record payment.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record payment"
      description={`${expense.referenceNo} · ${expense.payee}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={recordPayment.isPending} onClick={() => void confirm()}>
            Record payment
          </Button>
        </>
      }
    >
      {error ? <p className="edlab-error mb-4">{error}</p> : null}
      <p className="edlab-notice mb-4">
        {formatMoney(expense.unpaidAmount)} is approved and unpaid. You can record a full or
        partial payment.
      </p>
      <div className="form-grid">
        <label className="form-field">
          Payment amount · PKR
          <input
            type="number"
            min="0.01"
            step="0.01"
            max={expense.unpaidAmount}
            className="edlab-input"
            value={amount}
            placeholder={expense.unpaidAmount}
            onChange={(event) => {
              setAmount(event.target.value);
            }}
          />
        </label>
        <label className="form-field">
          Actual payment date
          <input
            type="date"
            className="edlab-input"
            value={paymentDate}
            onChange={(event) => {
              setPaymentDate(event.target.value);
            }}
          />
        </label>
        <label className="form-field">
          Paid from
          <select
            className="edlab-select"
            value={bankAccountId}
            onChange={(event) => {
              setBankAccountId(event.target.value);
            }}
          >
            {bankAccounts.map((bank) => (
              <option key={bank.id} value={bank.id}>
                {bank.name}
              </option>
            ))}
          </select>
        </label>
        <label className="form-field">
          Bank / payment reference
          <input
            className="edlab-input"
            value={reference}
            placeholder="Transfer or cheque reference"
            onChange={(event) => {
              setReference(event.target.value);
            }}
          />
        </label>
      </div>
    </Modal>
  );
}
